<div class="space-y-6 pb-10">
  <div class="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
    <div>
      <h2 class="text-3xl font-black tracking-tight">Order Lookup</h2>
      <p class="mt-1 text-xs font-bold uppercase tracking-widest opacity-40">
        Search by order ID, order number, or rider ID
      </p>
    </div>
    <a routerLink="/orders" class="btn btn-sm btn-ghost rounded-xl">
      <i class="fa-solid fa-arrow-left"></i>
      Back to orders
    </a>
  </div>

  <div class="rounded-3xl border border-base-200 bg-base-100 p-5 shadow-sm">
    <div class="flex flex-col gap-4 lg:flex-row">
      <label class="form-control flex-1">
        <div class="label py-0">
          <span class="label-text text-[10px] font-black uppercase tracking-widest opacity-50">
            Order ID / Order number / Rider ID (UUID)
          </span>
        </div>
        <input
          type="text"
          class="input input-bordered rounded-2xl font-mono text-sm"
          placeholder="e.g. 1042 or ORD-1042 or rider UUID"
          [(ngModel)]="searchQuery"
          (keyup.enter)="lookup()"
        />
      </label>
      <button
        class="btn btn-primary rounded-2xl px-8"
        (click)="lookup()"
        [disabled]="isLoading()"
      >
        @if (isLoading()) {
          <span class="loading loading-spinner loading-sm"></span>
        } @else {
          <i class="fa-solid fa-magnifying-glass"></i>
        }
        Lookup
      </button>
    </div>
    <p class="mt-3 text-[11px] font-bold opacity-45">
      Tip: <span class="font-mono">orders.rider_id</span> is the rider record ID from the
      <span class="font-mono">riders</span> table. You can paste that UUID to list all orders for that rider.
    </p>
  </div>

  @if (lookupResult(); as result) {
    <div class="rounded-2xl border border-base-200 bg-base-100 px-4 py-3">
      <p class="text-xs font-black uppercase tracking-widest text-primary">
        {{ lookupTypeLabel(result.lookupType) }}
      </p>
      @if (result.message) {
        <p class="mt-1 text-xs font-bold opacity-50">{{ result.message }}</p>
      }
      <p class="mt-1 text-[11px] font-bold opacity-40">
        {{ result.orders.length }} order(s) found
      </p>
    </div>

    @if (result.orders.length > 1) {
      <div class="rounded-3xl border border-base-200 bg-base-100 overflow-hidden">
        <div class="border-b border-base-200 px-5 py-4">
          <h3 class="text-sm font-black uppercase tracking-widest opacity-60">Select an order</h3>
        </div>
        <div class="divide-y divide-base-200">
          @for (order of result.orders; track order.order_id) {
            <button
              type="button"
              class="flex w-full items-center justify-between gap-4 px-5 py-4 text-left transition hover:bg-base-200/40"
              [class.bg-primary/5]="selectedOrder()?.order_id === order.order_id"
              (click)="selectOrder(order)"
            >
              <div>
                <p class="text-sm font-black">
                  #{{ order.order_number || order.order_id }}
                </p>
                <p class="mt-1 text-[11px] font-bold opacity-50">
                  {{ order.current_status }} · Rs {{ order.payable_amount | number:'1.0-0' }}
                </p>
                <p class="mt-1 text-[10px] font-mono opacity-40">
                  Rider: {{ riderDisplayName(order) }}
                </p>
              </div>
              <span class="text-[10px] font-mono opacity-35">
                {{ order.created_at | istDate:'short' }}
              </span>
            </button>
          }
        </div>
      </div>
    }
  }

  @if (selectedOrder(); as selected) {
    <div class="rounded-3xl border border-base-200 bg-base-100 p-5 shadow-sm">
      <div class="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <p class="text-[10px] font-black uppercase tracking-[0.2em] text-primary">Order details</p>
          <h3 class="text-2xl font-black">
            #{{ selected.order_number || selected.order_id }}
          </h3>
          <p class="mt-1 text-xs font-bold opacity-50">
          <div class="rounded-2xl border border-base-200 overflow-hidden">
            <div class="flex items-center justify-between border-b border-base-200 px-5 py-4">
              <h4 class="text-sm font-black uppercase tracking-widest opacity-60">Items</h4>
              <span class="badge badge-sm font-black">
                {{ selected.summary.items }} lines / {{ selected.summary.quantity }} qty
              </span>
            </div>
            <div class="divide-y divide-base-200">
              @for (item of selected.items; track item.sku + $index) {
                <div class="flex gap-4 p-5">
                  <div class="h-16 w-16 shrink-0 overflow-hidden rounded-xl border border-base-300 bg-base-200">
                    @if (item.image) {
                      <img [src]="item.image" [alt]="item.product_name" class="h-full w-full object-cover" />
                    } @else {
                      <div class="flex h-full w-full items-center justify-center">
                        <i class="fa-solid fa-box text-xl opacity-30"></i>
                      </div>
                    }
                  </div>
                  <div class="min-w-0 flex-1">
                    <p class="text-sm font-black leading-snug">{{ item.product_name }}</p>
                    <div class="mt-2 flex flex-wrap gap-2">
                      <span class="rounded-lg bg-base-200 px-2 py-1 text-[10px] font-mono opacity-70">
                        SKU: {{ item.sku || '-' }}
                      </span>
                      <span class="rounded-lg bg-primary/10 px-2 py-1 text-[10px] font-black uppercase text-primary">
                        {{ item.variant_name || 'N/A' }}
                      </span>
                      <span class="rounded-lg bg-base-200 px-2 py-1 text-[10px] font-black">
                        Qty {{ item.order_quantity }}
                      </span>
                    </div>
                  </div>
                  <div class="text-right">
                    <p class="text-sm font-black">Rs {{ item.price | number:'1.0-0' }}</p>
                    <p class="mt-1 text-sm font-black text-primary">Rs {{ item.total | number:'1.0-0' }}</p>
                  </div>
                </div>
              }
            </div>
          </div>

          <div class="rounded-2xl border border-base-200 p-5">
            <h4 class="mb-5 text-sm font-black uppercase tracking-widest opacity-60">Status history</h4>
            <div class="space-y-5">
              @for (history of selected.status_history; track history.created_at + history.status) {
                <div class="flex items-start gap-4">
                  <div class="mt-1 h-3 w-3 rounded-full bg-primary ring-4 ring-primary/10"></div>
                  <div class="flex-1">
                    <p class="text-xs font-black uppercase tracking-widest text-primary">{{ history.status }}</p>
                    <p class="mt-1 text-[11px] font-bold opacity-50">
                      {{ history.note || 'Status updated' }}
                      @if (history.actor) { by {{ history.actor }} }
                    </p>
                  </div>
                  <span class="text-[10px] font-mono opacity-40">{{ history.created_at | istDate:'short' }}</span>
                </div>
              }
            </div>
          </div>
        </div>

        <div class="space-y-6">
          <div class="rounded-2xl border border-base-200 p-5">
            <h4 class="mb-5 text-sm font-black uppercase tracking-widest opacity-60">Store & rider summary</h4>
            <div class="space-y-4 text-sm">
              <div>
                <p class="text-[10px] font-black uppercase tracking-widest opacity-35">Store</p>
                <p class="font-black">{{ selected.store?.name || selected.store?.store_name || '—' }}</p>
                <p class="text-[10px] font-mono opacity-40">Store ID {{ selected.store?.store_id || '—' }}</p>
              </div>
              <div>
                <p class="text-[10px] font-black uppercase tracking-widest opacity-35">Rider</p>
                <p class="font-black">{{ riderDisplayName(selected) }}</p>
                <p class="text-[10px] font-mono opacity-40 break-all">
                  rider_id: {{ selected.rider_id || '—' }}
                </p>
                @if (selected.rider?.vehicle_type) {
                  <p class="text-[10px] font-bold uppercase opacity-45">{{ selected.rider?.vehicle_type || 'Vehicle not set' }}</p>
                }
                @if (selected.assigned_at) {
                  <p class="mt-1 text-[10px] opacity-45">Assigned {{ selected.assigned_at | istDate:'short' }}</p>
                }
              </div>
            </div>
          </div>

          <div class="rounded-2xl border border-base-200 p-5">
            <div class="mb-5 flex items-center justify-between gap-3">
              <h4 class="text-sm font-black uppercase tracking-widest opacity-60">Assigned rider details</h4>
              @if (selected.rider?.presence) {
              <span
                class="badge badge-sm border-0 font-black uppercase"
                [class.badge-success]="selected.rider?.presence?.is_online"
                [class.badge-ghost]="!selected.rider?.presence?.is_online"
              >
                {{ selected.rider?.presence?.is_online ? 'Online' : 'Offline' }}
              </span>
              }
            </div>

            @if (selected.rider; as rider) {
            <div class="space-y-5">
              <div class="flex items-center gap-4">
                <div class="h-14 w-14 shrink-0 overflow-hidden rounded-2xl border border-base-300 bg-primary/10">
                  @if (rider.profile_photo_url) {
                  <img
                    [src]="rider.profile_photo_url"
                    [alt]="rider.full_name || rider.name || 'Assigned rider'"
                    class="h-full w-full object-cover"
                  />
                  } @else {
                  <div class="flex h-full w-full items-center justify-center text-xl font-black text-primary">
                    {{ (rider.full_name || rider.name || 'R')[0] }}
                  </div>
                  }
                </div>
                <div class="min-w-0 flex-1">
                  <p class="truncate text-base font-black">{{ rider.full_name || rider.name || 'Rider' }}</p>
                  <p class="text-xs font-bold opacity-50">{{ rider.code || 'No rider code' }}</p>
                  <div class="mt-2 flex flex-wrap gap-2">
                    <span class="badge badge-sm badge-outline uppercase">{{ rider.vehicle_type || 'Vehicle not set' }}</span>
                    <span class="badge badge-sm badge-outline uppercase">{{ rider.status || 'Unknown' }}</span>
                    <span
                      class="badge badge-sm border-0 uppercase"
                      [class.badge-success]="rider.verification_status?.toLowerCase() === 'approved'"
                      [class.badge-warning]="rider.verification_status?.toLowerCase() !== 'approved'"
                    >
                      {{ rider.verification_status || 'Not verified' }}
                    </span>
                  </div>
                </div>
              </div>

              <div class="grid grid-cols-1 gap-3 text-xs sm:grid-cols-2">
                <div class="rounded-xl bg-base-200/60 p-3">
                  <p class="text-[9px] font-black uppercase tracking-widest opacity-40">Phone</p>
                  @if (rider.account?.phone) {
                  <a class="mt-1 block font-mono font-black text-primary" [href]="'tel:' + rider.account?.phone">
                    {{ rider.account?.phone }}
                  </a>
                  } @else {
                  <p class="mt-1 font-bold opacity-50">Not available</p>
                  }
                </div>
                <div class="rounded-xl bg-base-200/60 p-3">
                  <p class="text-[9px] font-black uppercase tracking-widest opacity-40">Email</p>
                  <p class="mt-1 break-all font-bold">{{ rider.account?.email || 'Not available' }}</p>
                </div>
                <div class="rounded-xl bg-base-200/60 p-3">
                  <p class="text-[9px] font-black uppercase tracking-widest opacity-40">Rider ID</p>
                  <p class="mt-1 break-all font-mono text-[10px] font-bold">{{ rider.id }}</p>
                </div>
                <div class="rounded-xl bg-base-200/60 p-3">
                  <p class="text-[9px] font-black uppercase tracking-widest opacity-40">User ID</p>
                  <p class="mt-1 break-all font-mono text-[10px] font-bold">{{ rider.user_id || '-' }}</p>
                </div>
                <div class="rounded-xl bg-base-200/60 p-3">
                  <p class="text-[9px] font-black uppercase tracking-widest opacity-40">Presence</p>
                  <p class="mt-1 font-black uppercase">{{ rider.presence?.status || 'Unknown' }}</p>
                </div>
                <div class="rounded-xl bg-base-200/60 p-3">
                  <p class="text-[9px] font-black uppercase tracking-widest opacity-40">Last seen</p>
                  <p class="mt-1 font-bold">
                    {{ rider.presence?.last_seen_at ? (rider.presence?.last_seen_at | istDate:'short') : 'Not available' }}
                  </p>
                </div>
              </div>

              @if (rider.presence?.latitude !== null && rider.presence?.latitude !== undefined && rider.presence?.longitude !== null && rider.presence?.longitude !== undefined) {
              <div class="rounded-xl border border-info/20 bg-info/5 p-3">
                <p class="text-[9px] font-black uppercase tracking-widest text-info">Latest rider location</p>
                <p class="mt-1 font-mono text-[10px] font-bold">
                  {{ rider.presence?.latitude }}, {{ rider.presence?.longitude }}
                </p>
              </div>
              }

              <div class="space-y-2 border-t border-base-200 pt-4 text-xs">
                <div class="flex justify-between gap-3">
                  <span class="opacity-50">Assigned</span>
                  <span class="text-right font-bold">{{ selected.assigned_at ? (selected.assigned_at | istDate:'short') : 'Not recorded' }}</span>
                </div>
                @if (selected.deliveries?.[0]; as delivery) {
                <div class="flex justify-between gap-3">
                  <span class="opacity-50">Delivery status</span>
                  <span class="font-black uppercase">{{ delivery.status || '-' }}</span>
                </div>
                <div class="flex justify-between gap-3">
                  <span class="opacity-50">Picked up</span>
                  <span class="text-right font-bold">{{ delivery.picked_at ? (delivery.picked_at | istDate:'short') : 'Not yet' }}</span>
                </div>
                <div class="flex justify-between gap-3">
                  <span class="opacity-50">Distance / ETA</span>
                  <span class="text-right font-bold">
                    {{ delivery.estimated_distance_km ?? '-' }} km / {{ delivery.estimated_delivery_minutes ?? '-' }} min
                  </span>
                </div>
                <div class="flex justify-between gap-3">
                  <span class="opacity-50">Rider payout</span>
                  <span class="font-bold">Rs {{ (delivery.final_payout ?? delivery.estimated_payout ?? 0) | number:'1.0-0' }}</span>
                </div>
                }
              </div>
            </div>
            } @else if (selected.rider_id) {
            <div class="rounded-xl border border-warning/20 bg-warning/5 p-4">
              <p class="break-all text-sm font-black">Rider ID: {{ selected.rider_id }}</p>
              <p class="mt-1 text-xs opacity-60">The rider record could not be loaded.</p>
            </div>
            } @else {
            <div class="rounded-xl border-2 border-dashed border-base-200 p-5 text-center">
              <i class="fa-solid fa-motorcycle text-2xl opacity-25"></i>
              <p class="mt-2 text-xs font-black uppercase opacity-40">No rider assigned</p>
            </div>
            }
          </div>

          <div class="rounded-2xl border border-base-200 p-5">
            <h4 class="mb-5 text-sm font-black uppercase tracking-widest opacity-60">Customer & delivery</h4>
            <div class="space-y-4">
              <div>
                <p class="text-[10px] font-black uppercase tracking-widest opacity-35">Customer</p>
                <p class="text-sm font-black">
                  {{ selected.customer.receiver_name || selected.customer.name || 'Customer' }}
                </p>
                <p class="text-xs font-mono opacity-60">{{ selected.customer.phone || selected.delivery_phone }}</p>
              </div>
              <div>
                <p class="text-[10px] font-black uppercase tracking-widest opacity-35">Address</p>
                <p class="mt-1 text-xs font-bold leading-relaxed">{{ selected.delivery_address }}</p>
                @if (selected.delivery?.lat && selected.delivery?.lng) {
                  <p class="mt-2 text-[10px] font-mono opacity-45">
                    Lat {{ selected.delivery?.lat }}, Lng {{ selected.delivery?.lng }}
                  </p>
                }
              </div>
            </div>
          </div>

          <div class="rounded-2xl border border-base-200 p-5">
            <h4 class="mb-5 text-sm font-black uppercase tracking-widest opacity-60">Pricing</h4>
            <div class="space-y-3 text-sm">
              <div class="flex justify-between">
                <span class="opacity-55">Subtotal</span>
                <span class="font-black">Rs {{ (selected.pricing?.subtotal ?? selected.total_amount) | number:'1.0-0' }}</span>
              </div>
              <div class="flex justify-between">
                <span class="opacity-55">Discount</span>
                <span class="font-black text-success">
                  - Rs {{ (selected.pricing?.discount_amount || getPromotionTotal(selected)) | number:'1.0-0' }}
                </span>
              </div>
              <div class="flex justify-between">
                <span class="opacity-55">Delivery fee</span>
                <span class="font-black">Rs {{ (selected.pricing?.delivery_fee || 0) | number:'1.0-0' }}</span>
              </div>
              <div class="divider my-1"></div>
              <div class="flex justify-between text-xl font-black">
                <span class="opacity-45">Payable</span>
                <span class="text-primary">
                  Rs {{ (selected.pricing?.payable_amount ?? selected.payable_amount) | number:'1.0-0' }}
                </span>
              </div>
              @if (selected.payment_method) {
                <p class="text-[10px] font-bold uppercase opacity-45">
                  {{ selected.payment_method }}
                  @if (selected.payment_reference) {
                    · {{ selected.payment_reference }}
                  }
                </p>
              }
            </div>
          </div>

          @if ((selected.deliveries || []).length > 0) {
            <div class="rounded-2xl border border-info/20 bg-info/5 p-5">
              <h4 class="mb-4 text-sm font-black uppercase tracking-widest text-info">Delivery record</h4>
              @for (delivery of selected.deliveries; track delivery.id) {
                <div class="space-y-2 text-xs">
                  <div class="flex justify-between">
                    <span class="opacity-55">Status</span>
                    <span class="font-black uppercase">{{ delivery.status }}</span>
                  </div>
                  @if (delivery.estimated_distance_km) {
                    <div class="flex justify-between">
                      <span class="opacity-55">Distance</span>
                      <span class="font-black">{{ delivery.estimated_distance_km }} km</span>
                    </div>
                  }
                  @if (delivery.estimated_delivery_minutes) {
                    <div class="flex justify-between">
                      <span class="opacity-55">ETA</span>
                      <span class="font-black">{{ delivery.estimated_delivery_minutes }} min</span>
                    </div>
                  }
                </div>
              }
            </div>
          }
        </div>
      </div>
    </div>
  }
</div>
